import { useState, useEffect, useRef, type ReactNode } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, SegmentedControl } from "@/components/padroes";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { formatCNPJ, formatCents, formatMesRef } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNF } from "@/context/NFContext";
import type { NotaFiscal } from "@/data/mock";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  Check,
  FileText,
  Info,
  Loader2,
  Send,
  Trophy,
  Upload,
  Wallet,
} from "lucide-react";

type TipoNF = "salario" | "bonificacao";

interface InstrucaoAtiva {
  competencia: string;
  tomadorNome: string;
  valor: number;
  nfStatus: string | null;
}

/** Bonificação de metas liberada (competência fechada) no mês. Centavos. */
interface BonificacaoAtiva {
  competencia: string;
  nome: string;
  valor: number;
  valorBruto: number;
  descontoEmprestimo: number;
  ajusteManual: number;
  tomadorNome: string | null;
  parcela: number;
  numeroParcelas: number;
  nfStatus: string | null;
}

interface RespostaInstrucoes {
  instrucao: InstrucaoAtiva | null;
  bonificacao: BonificacaoAtiva | null;
}

interface DadosExtraidos {
  mesReferencia: string | null;
  numero: string | null;
  valor: number | null;
  descricao: string | null;
  cnpjTomador: string | null;
}

function formatBRLInput(val: number | null) {
  if (val === null) return "";
  return val.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const soDigitos = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Aviso em caixa (perfil incompleto, envio bloqueado…). */
const Aviso = ({ tom, titulo, children }: { tom: "alerta" | "erro"; titulo: string; children: ReactNode }) => (
  <div
    className={cn(
      "flex gap-3 rounded-xl border p-4",
      tom === "alerta" ? "border-amber-500/40 bg-amber-500/5" : "border-destructive/40 bg-destructive/5",
    )}
  >
    <AlertTriangle className={cn("mt-0.5 h-5 w-5 shrink-0", tom === "alerta" ? "text-amber-500" : "text-destructive")} />
    <div className="text-sm">
      <div className={cn("font-semibold", tom === "alerta" ? "text-amber-800 dark:text-amber-400" : "text-destructive")}>
        {titulo}
      </div>
      {children}
    </div>
  </div>
);

/** Campo extraído do PDF, só leitura; borda verde + mensagem quando confere. */
const CampoExtraido = ({
  rotulo,
  confere,
  className,
  children,
}: {
  rotulo: string;
  confere?: string | false;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <Label className="text-[13px]">{rotulo}</Label>
    {children}
    {confere && (
      <span className="flex items-center gap-1 text-xs text-success">
        <Check className="h-3 w-3" />
        {confere}
      </span>
    )}
  </div>
);

const NovaNF = () => {
  const { user } = useAuth();
  const { addNota } = useNF();
  const nav = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [extraindo, setExtraindo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dados, setDados] = useState<DadosExtraidos | null>(null);
  const [nfsRecusadas, setNfsRecusadas] = useState<NotaFiscal[] | null>(null);
  const [instrucaoAtiva, setInstrucaoAtiva] = useState<InstrucaoAtiva | null>(null);
  const [bonificacao, setBonificacao] = useState<BonificacaoAtiva | null>(null);
  const [tipo, setTipo] = useState<TipoNF>("salario");
  const [arrastando, setArrastando] = useState(false);
  const nomeBonificacao = useNomeBonificacao();

  useEffect(() => {
    api.get<NotaFiscal[]>("/nfs/pendentes-cancelamento")
      .then(setNfsRecusadas)
      .catch(() => setNfsRecusadas([]));
    api.get<RespostaInstrucoes>("/instrucoes/me")
      .then((r) => {
        setInstrucaoAtiva(r?.instrucao ?? null);
        setBonificacao(r?.bonificacao ?? null);
      })
      .catch(() => {});
  }, []);

  // Comum ao clique e ao arrastar: lê o PDF e preenche os campos.
  const processarArquivo = async (file: File) => {
    setArquivo(file);
    setDados(null);
    setExtraindo(true);

    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      const result = await api.postForm<DadosExtraidos>("/nfs/extrair", fd);
      setDados(result);

      const camposFaltando = [
        !result.mesReferencia && "mês de referência",
        !result.numero && "número da NF",
        result.valor === null && "valor",
        !result.descricao && "descrição",
      ].filter(Boolean);

      if (camposFaltando.length > 0) {
        toast.warning(`Não foi possível extrair: ${camposFaltando.join(", ")}`);
      } else {
        toast.success("Dados extraídos com sucesso");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao processar PDF");
      setArquivo(null);
    } finally {
      setExtraindo(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await processarArquivo(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setArrastando(false);
    if (extraindo) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void processarArquivo(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!arquivo || !dados) {
      toast.error("Faça o upload do PDF primeiro");
      return;
    }
    if (!dados.mesReferencia || !dados.numero || dados.valor === null || !dados.descricao) {
      toast.error("Alguns campos não foram extraídos do PDF. Verifique o arquivo.");
      return;
    }

    const fd = new FormData();
    fd.append("arquivo", arquivo);
    fd.append("cnpj", user?.cnpj ?? "");
    fd.append("mesReferencia", dados.mesReferencia);
    fd.append("tipo", tipo);
    fd.append("numero", dados.numero);
    fd.append("valor", String(dados.valor));
    fd.append("descricao", dados.descricao);
    fd.append("departamento", user?.department ?? "");
    if (dados.cnpjTomador) fd.append("cnpjTomador", dados.cnpjTomador);

    setSubmitting(true);
    try {
      await addNota(fd);
      toast.success(
        tipo === "bonificacao"
          ? `NF de ${nomeBonificacao.toLowerCase()} enviada! Aguardando validação OCR.`
          : "NF enviada! Aguardando validação OCR.",
      );
      nav("/colaborador/notas");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar NF");
    } finally {
      setSubmitting(false);
    }
  };

  const pronto = !!arquivo && !extraindo && !!dados;

  const perfilIncompleto = !user?.cnpj || !user?.razaoSocial;
  const departamentoIndefinido = !user?.department;
  const bloqueadoPorRecusa = !!nfsRecusadas && nfsRecusadas.length > 0;
  // Sem bonificação liberada no mês não há o que emitir na aba de bonificação.
  const semBonificacao = tipo === "bonificacao" && !bonificacao;
  const bloqueado = perfilIncompleto || departamentoIndefinido || bloqueadoPorRecusa || semBonificacao;
  const esperado =
    tipo === "bonificacao"
      ? bonificacao && { competencia: bonificacao.competencia, valor: bonificacao.valor }
      : instrucaoAtiva;

  // Conferências do que foi lido no PDF com o cadastro e com o valor a emitir.
  const mesmaCompetencia = !!esperado && dados?.mesReferencia === esperado.competencia;
  const valorConfere = mesmaCompetencia && dados?.valor != null && Math.round(dados.valor * 100) === esperado.valor;
  const valorDivergente = mesmaCompetencia && dados?.valor != null && Math.round(dados.valor * 100) !== esperado.valor;
  const tomadorConfere =
    !!dados?.cnpjTomador && !!user?.cnpjContratante && soDigitos(dados.cnpjTomador) === soDigitos(user.cnpjContratante);
  const extracaoCompleta = !!dados && !!dados.mesReferencia && !!dados.numero && dados.valor !== null && !!dados.descricao;

  const trocarTipo = (novo: TipoNF) => {
    setTipo(novo);
    setArquivo(null);
    setDados(null);
  };

  // CLT não emite NF: a página dele é o holerite.
  if (user?.vinculo === "clt") return <Navigate to="/colaborador/demonstrativo" replace />;

  const tomadorNome = user?.tomadorNome ?? instrucaoAtiva?.tomadorNome ?? bonificacao?.tomadorNome ?? null;

  return (
    <RoleGuard role={["colaborador", "rh", "financeiro"]}>
      <AppLayout title="Enviar nova NF">
        <div className="mx-auto flex max-w-[980px] flex-col gap-[18px] tabular-nums">
          <PageHeader
            titulo="Enviar nova NF"
            subtitulo="Anexe o PDF: os dados são lidos automaticamente e você só confere."
          />

          <SegmentedControl<TipoNF>
            aria-label="Tipo da NF"
            value={tipo}
            onChange={trocarTipo}
            className="grid w-full max-w-[480px] grid-cols-2 [&>button]:flex [&>button]:h-9 [&>button]:items-center [&>button]:justify-center [&>button]:gap-2 [&>button]:text-sm"
            options={[
              { value: "salario", label: <><Wallet className="h-[15px] w-[15px]" />Salário</> },
              { value: "bonificacao", label: <><Trophy className="h-[15px] w-[15px]" />{nomeBonificacao}</> },
            ]}
          />

          {perfilIncompleto && (
            <Aviso tom="alerta" titulo="Perfil incompleto">
              <p className="mt-1">
                Preencha seu <strong>CNPJ</strong> e <strong>Razão Social</strong> no perfil antes de enviar uma NF.
                Esses dados são obrigatórios para validação automática.
              </p>
              <Link to="/perfil" className="mt-2 inline-block text-xs font-medium text-amber-700 underline dark:text-amber-400">
                Ir para o perfil →
              </Link>
            </Aviso>
          )}

          {departamentoIndefinido && (
            <Aviso tom="alerta" titulo="Departamento não informado">
              <p className="mt-1">
                Seu perfil não possui <strong>departamento</strong> definido. Solicite ao RH antes de enviar uma NF.
              </p>
              <Link to="/perfil" className="mt-2 inline-block text-xs font-medium text-amber-700 underline dark:text-amber-400">
                Ir para o perfil →
              </Link>
            </Aviso>
          )}

          {bloqueadoPorRecusa && (
            <Aviso tom="erro" titulo="Envio bloqueado">
              <p className="mt-1">
                Você possui {nfsRecusadas.length === 1 ? "uma nota recusada" : `${nfsRecusadas.length} notas recusadas`} sem prova de cancelamento.
                Envie a nota cancelada antes de submeter uma nova.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {nfsRecusadas.map((n) => (
                  <Link key={n.id} to={`/colaborador/notas/${n.id}`} className="text-xs font-medium text-destructive underline">
                    NF nº {n.numero}
                  </Link>
                ))}
              </div>
            </Aviso>
          )}

          <div className="flex flex-wrap items-start gap-[18px]">
            {/* O que emitir */}
            <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-3">
              {tipo === "salario" ? (
                <section className="flex flex-col gap-3 rounded-xl border border-primary/35 bg-card px-5 py-[18px]">
                  <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-primary-text">
                    <CalendarDays className="h-3.5 w-3.5" />
                    O que emitir{instrucaoAtiva && ` · ${formatMesRef(instrucaoAtiva.competencia)}`}
                  </span>
                  <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Tomador</dt>
                    <dd className="text-right font-medium">{tomadorNome ?? "—"}</dd>
                    <dt className="text-muted-foreground">CNPJ do tomador</dt>
                    <dd className="text-right">{user?.cnpjContratante ? formatCNPJ(user.cnpjContratante) : "—"}</dd>
                    <dt className="text-muted-foreground">Competência</dt>
                    <dd className="text-right">{instrucaoAtiva ? capitalizar(formatMesRef(instrucaoAtiva.competencia)) : "—"}</dd>
                  </dl>
                  <div className="flex items-baseline justify-between gap-3 border-t pt-3">
                    <span className="text-sm font-medium">Valor da NF</span>
                    <span className="text-[22px] font-semibold">
                      {instrucaoAtiva ? formatCents(instrucaoAtiva.valor) : "—"}
                    </span>
                  </div>
                  {!instrucaoAtiva && (
                    <p className="text-xs text-muted-foreground">
                      O valor aparece aqui quando o RH fecha a folha do mês.
                    </p>
                  )}
                  {instrucaoAtiva?.nfStatus && (
                    <p className="text-xs text-muted-foreground">
                      Você já enviou esta NF (status: {instrucaoAtiva.nfStatus}).
                    </p>
                  )}
                  <Link
                    to={instrucaoAtiva ? `/colaborador/demonstrativo/${instrucaoAtiva.competencia}` : "/colaborador/demonstrativo"}
                    className="text-[13px] font-medium text-primary-text hover:underline"
                  >
                    Ver composição do valor →
                  </Link>
                </section>
              ) : bonificacao ? (
                <section className="flex flex-col gap-3 rounded-xl border border-primary/35 bg-card px-5 py-[18px]">
                  <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-primary-text">
                    <Trophy className="h-3.5 w-3.5" />
                    {nomeBonificacao} de {formatMesRef(bonificacao.competencia)}
                    {bonificacao.numeroParcelas > 1 && ` — parcela ${bonificacao.parcela}/${bonificacao.numeroParcelas}`}
                  </span>
                  <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Valor da parcela</dt>
                    <dd className="text-right">{formatCents(bonificacao.valorBruto)}</dd>
                    {bonificacao.ajusteManual !== 0 && (
                      <>
                        <dt className="text-muted-foreground">Ajuste</dt>
                        <dd className="text-right">{formatCents(bonificacao.ajusteManual)}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">Desconto de empréstimo</dt>
                    <dd className={cn("text-right", bonificacao.descontoEmprestimo > 0 && "text-destructive")}>
                      {bonificacao.descontoEmprestimo > 0 ? `− ${formatCents(bonificacao.descontoEmprestimo)}` : "—"}
                    </dd>
                  </dl>
                  <div className="flex items-baseline justify-between gap-3 border-t pt-3">
                    <span className="text-sm font-medium">Valor da NF</span>
                    <span className="text-[22px] font-semibold">{formatCents(bonificacao.valor)}</span>
                  </div>
                  {bonificacao.nfStatus && (
                    <p className="text-xs text-muted-foreground">
                      Você já enviou esta NF (status: {bonificacao.nfStatus}).
                    </p>
                  )}
                </section>
              ) : (
                <section className="flex flex-col gap-2 rounded-xl border bg-card px-5 py-[18px]">
                  <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    <Trophy className="h-3.5 w-3.5" />
                    {nomeBonificacao}
                  </span>
                  <span className="text-sm font-semibold">Nenhuma {nomeBonificacao.toLowerCase()} liberada este mês</span>
                </section>
              )}
              <div className="flex gap-2.5 rounded-[10px] bg-muted px-3.5 py-3 text-[13px] leading-snug text-foreground/80">
                <Info className="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  {tipo === "salario"
                    ? `A NF de ${nomeBonificacao.toLowerCase()} é separada: envie na outra aba quando a parcela for liberada.`
                    : `A NF de ${nomeBonificacao.toLowerCase()} só é enviada quando o RH libera a parcela do mês. A NF do seu salário vai na aba Salário.`}
                </span>
              </div>
            </aside>

            {/* Upload e dados extraídos */}
            <form
              onSubmit={handleSubmit}
              className={cn(
                "flex min-w-0 flex-[2_1_460px] flex-col gap-[18px] rounded-xl border bg-card px-5 py-5 sm:px-[22px]",
                bloqueado && "pointer-events-none select-none opacity-50",
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                disabled={extraindo}
                onChange={handleFileChange}
              />

              {!pronto ? (
                <button
                  type="button"
                  disabled={extraindo}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setArrastando(true);
                  }}
                  onDragLeave={() => setArrastando(false)}
                  onDrop={handleDrop}
                  className={cn(
                    "flex flex-col items-center gap-2.5 rounded-xl border-[1.5px] border-dashed px-5 py-10 text-center transition-colors",
                    extraindo
                      ? "cursor-wait border-primary/50 bg-primary/[0.04]"
                      : arrastando
                        ? "border-primary bg-primary/[0.04]"
                        : "border-input bg-background hover:border-primary hover:bg-primary/[0.04]",
                  )}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    {extraindo ? <Loader2 className="h-[22px] w-[22px] animate-spin" /> : <Upload className="h-[22px] w-[22px]" />}
                  </span>
                  <span className="text-[15px] font-semibold">
                    {extraindo ? "Extraindo dados da NF..." : "Arraste o PDF da NF ou clique para escolher"}
                  </span>
                  <span className="text-[13px] text-muted-foreground">
                    {extraindo && arquivo ? arquivo.name : "PDF, PNG ou JPG — máx. 10 MB"}
                  </span>
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-2.5 rounded-[10px] border bg-row-hover px-3 py-2.5">
                    <FileText className="h-[18px] w-[18px] shrink-0 text-primary" />
                    <span className="flex min-w-0 flex-1 flex-col gap-px">
                      <span className="truncate text-sm font-medium">{arquivo.name}</span>
                      {extracaoCompleta ? (
                        <span className="flex items-center gap-1 text-xs text-success">
                          <Check className="h-3 w-3" />
                          Dados extraídos do PDF
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                          <AlertTriangle className="h-3 w-3" />
                          Alguns dados não foram lidos do PDF
                        </span>
                      )}
                    </span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
                      Trocar arquivo
                    </Button>
                  </div>

                  {valorDivergente && (
                    <div className="flex gap-3 rounded-[10px] border border-amber-500/40 bg-amber-500/5 p-3.5">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      <div className="text-[13px]">
                        <span className="font-semibold text-amber-800 dark:text-amber-400">Atenção — valor divergente: </span>
                        O valor extraído (R$ {formatBRLInput(dados.valor)}) difere do valor{" "}
                        {tipo === "bonificacao" ? `da ${nomeBonificacao.toLowerCase()}` : "declarado pelo RH"} para
                        esta competência (R$ {formatBRLInput((esperado?.valor ?? 0) / 100)}). A nota será recusada automaticamente.
                      </div>
                    </div>
                  )}

                  <div className="grid gap-x-4 gap-y-3.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                    <CampoExtraido
                      rotulo="Tomador (contratante)"
                      className="col-span-full"
                      confere={tomadorConfere && "Confere com seu cadastro"}
                    >
                      <div
                        className={cn(
                          "flex min-h-10 items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm",
                          tomadorConfere && "border-success/50",
                        )}
                      >
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {user?.tomadorNome ? (
                          <span>
                            <span className="font-medium">{user.tomadorNome}</span>
                            {user.cnpjContratante && (
                              <span className="ml-2 font-mono text-muted-foreground">{user.cnpjContratante}</span>
                            )}
                          </span>
                        ) : (
                          <span className="italic text-muted-foreground">Nenhum tomador vinculado — solicite ao RH</span>
                        )}
                      </div>
                    </CampoExtraido>

                    <CampoExtraido rotulo="Mês de referência">
                      <Input value={dados.mesReferencia ? capitalizar(formatMesRef(dados.mesReferencia)) : ""} disabled className="disabled:opacity-100" />
                    </CampoExtraido>
                    <CampoExtraido rotulo="Número da NF">
                      <Input value={dados.numero ?? ""} disabled className="disabled:opacity-100" />
                    </CampoExtraido>
                    <CampoExtraido rotulo="Valor (R$)" confere={valorConfere && "Igual ao valor a emitir"}>
                      <Input
                        value={formatBRLInput(dados.valor)}
                        disabled
                        className={cn("disabled:opacity-100", valorConfere && "border-success/50")}
                      />
                    </CampoExtraido>
                    <CampoExtraido rotulo="Departamento">
                      <Input value={user?.department ?? "Não informado"} disabled className="disabled:opacity-100" />
                    </CampoExtraido>

                    <CampoExtraido rotulo="Descrição do serviço" className="col-span-full">
                      <Textarea value={dados.descricao ?? ""} disabled rows={3} className="disabled:opacity-100" />
                    </CampoExtraido>
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="h-11" onClick={() => nav(-1)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="h-11 flex-1 text-[15px]" disabled={submitting}>
                      {submitting ? <Loader2 className="animate-spin" /> : <Send />}
                      {submitting
                        ? "Enviando..."
                        : tipo === "bonificacao"
                          ? `Enviar NF de ${nomeBonificacao.toLowerCase()}`
                          : "Enviar NF"}
                    </Button>
                  </div>
                </>
              )}

              {/* Cancelar antes de carregar */}
              {!pronto && !extraindo && (
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => nav(-1)}>
                    Cancelar
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default NovaNF;
